import { Component } from '@theme/component';
import { request, getCart, isCartBusy, replacer, settings, replaceVariant } from '@theme/cart-controller';

class CartVariantEditor extends Component {
  requiredRefs = ['toggle', 'panel', 'select', 'status', 'save'];
  sourceLine = null;
  product = null;
  loading = false;
  controller = null;

  connectedCallback() { super.connectedCallback(); this.refs.toggle.hidden = false; }
  updatedCallback() {
    super.updatedCallback();
    this.controller?.abort();
    this.sourceLine = null;
    this.product = null;
    this.loading = false;
    this.refs.toggle.hidden = false;
    this.refs.toggle.disabled = false;
    this.refs.toggle.setAttribute('aria-expanded', 'false');
    this.refs.panel.hidden = true;
  }
  disconnectedCallback() { super.disconnectedCallback(); this.controller?.abort(); }

  async edit() {
    if (this.loading || isCartBusy()) return;
    if (replacer.recovery) { this.refs.status.textContent = settings.recovery; return; }
    this.loading = true;
    this.refs.toggle.disabled = true;
    this.refs.panel.hidden = false;
    this.refs.toggle.setAttribute('aria-expanded', 'true');
    this.refs.status.textContent = settings.loading;
    this.refs.save.disabled = true;
    this.controller?.abort();
    this.controller = new AbortController();
    try {
      const url = new URL(this.dataset.productUrl, location.origin);
      url.search = '';
      url.pathname += '.js';
      const [cart, product] = await Promise.all([getCart(), request(url.href, { signal: this.controller.signal, cache: 'no-store' })]);
      if (!this.isConnected) return;
      const line = cart.items.find((item) => item.key === this.dataset.lineKey);
      if (!line) throw new Error(settings.sourceChanged);
      this.sourceLine = line;
      this.product = product;
      this.refs.select.replaceChildren();
      const plan = line.selling_plan_allocation?.selling_plan?.id;
      for (const variant of product.variants) {
        const supportedPlan = !plan || variant.selling_plan_allocations?.some((allocation) => String(allocation.selling_plan_id) === String(plan));
        const option = new Option(`${variant.title}${!variant.available || !supportedPlan ? ` — ${settings.unavailableLabel}` : ''}`, String(variant.id));
        option.disabled = !variant.available || !supportedPlan;
        option.selected = String(variant.id) === String(line.variant_id);
        this.refs.select.add(option);
      }
      this.refs.status.textContent = settings.keepQuantity.replace('[quantity]', String(line.quantity));
      this.selectionChanged();
      this.refs.select.focus();
    } catch (error) {
      if (error.name !== 'AbortError' && this.isConnected) this.refs.status.textContent = error.message || settings.error;
    } finally {
      this.loading = false;
      if (this.isConnected) this.refs.toggle.disabled = false;
    }
  }

  selectionChanged() {
    const selected = this.refs.select.selectedOptions[0];
    this.refs.save.disabled = !this.sourceLine || !selected || selected.disabled || selected.value === String(this.sourceLine.variant_id);
  }

  cancel() {
    if (isCartBusy()) return;
    this.controller?.abort();
    this.refs.panel.hidden = true;
    this.refs.toggle.setAttribute('aria-expanded', 'false');
    this.refs.status.textContent = '';
    this.refs.toggle.focus();
  }

  async save() {
    if (this.loading || isCartBusy() || !this.sourceLine || this.refs.save.disabled) return;
    const target = this.product?.variants.find((variant) => String(variant.id) === this.refs.select.value);
    const plan = this.sourceLine.selling_plan_allocation?.selling_plan?.id;
    if (!target?.available || (plan && !target.selling_plan_allocations?.some((allocation) => String(allocation.selling_plan_id) === String(plan)))) {
      this.refs.status.textContent = settings.unavailable;
      return;
    }
    this.refs.save.disabled = true;
    this.refs.status.textContent = settings.saving;
    await replaceVariant(this.sourceLine, target.id);
  }
}
if (!customElements.get('cart-variant-editor')) customElements.define('cart-variant-editor', CartVariantEditor);
